import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createSelector } from "reselect"
import { getCurrentAgenda, getRecordEditId } from "../../../router/selectors"
import { getReportDatapointTitle, getReportsStateObject } from "../selectors"
import type { StateObject } from "../../../redux/types"
import type { OrderEmailSendingState, OrderPagesObject, OrdersState } from "../reducer"
import {
    getFilters,
    getSelectedLocationIdsFromGlobalFilter,
    getTimezoneForQueryParam,
} from "../../../filters/selectors"
import type { OrderReportApiParameters, OrderReportItem } from "./types"
import { OrderDetailForClipboard } from "./types"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { MainMenuAgendaEnum } from "../../../router/types"
import type { OrderDetail } from "../../../../graphql/types"
import type { Dictionary } from "../../../../utils/types"
import { PaymentSummaryRowTitles } from "./components/PaymentSummaryComponent"
import { getLocationsAuthData, getSelectedAccount } from "../../../auth/selectors"
import { isEmpty } from "ramda"
import momentTimezone from "moment-timezone"

export const getOrdersState = (state: StateObject): OrdersState => getReportsStateObject(state).orders

export const getOrdersPages = (state: StateObject): OrderPagesObject => getOrdersState(state).pages

export const getOrdersCurrentPage = (state: StateObject): number => getFilters(state, "orders").pagination.currentPage

export const getOrdersByPageNumber = (state: StateObject, page: number): Maybe<OrderReportItem[]> =>
    getOrdersPages(state)[page]

export const getOrdersInCurrentPage: (_arg0: StateObject) => OrderReportItem[] = createSelector(
    getOrdersPages,
    getOrdersCurrentPage,
    (pages: OrderPagesObject, current: number) => pages[current] || []
)

export const isOrderReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.OrdersReport

export const getEditOrderId = (state: StateObject) => getRecordEditId(state)

export const getDataForOrdersReportTable = (state: StateObject): OrderReportItem[] => getOrdersInCurrentPage(state)

export const getOrderDetail = (state: StateObject): Maybe<OrderDetail> => getOrdersState(state).orderDetail

export const getOrderSendingEmailState = (state: StateObject): Maybe<OrderEmailSendingState> =>
    getOrdersState(state).emailSendingState

export const getOrdersQueryApiParam = (state: StateObject): OrderReportApiParameters => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const filters = getFilters(state, "orders")
    const offset = (filters.pagination.currentPage - 1) * (filters.pagination.pageLimit ?? 0)
    const from = globalFilters.date.from
    const to = globalFilters.date.to

    const locationIds = getSelectedLocationIdsFromGlobalFilter(state)
    const timezone = getTimezoneForQueryParam(state, locationIds)

    // Construct parameters
    let result = {
        from: from,
        to: to,
        timezone: timezone,
        location_ids: locationIds.length > 0 ? locationIds.join() : "",
        count: filters.pagination.pageLimit ?? 100,
        offset: offset ?? 0,
        ...(filters.assignee_id && filters.assignee_id.length > 0
            ? {
                  assignee_ids: filters.assignee_id.join(","),
              }
            : null),
        ...(filters.search && filters.search.value.length > 0
            ? {
                  search: filters.search.value,
              }
            : null),
        ...(filters.sort && filters.sort.by
            ? {
                  sort: filters.sort.by.toLowerCase(),
              }
            : null),
        ...(filters.sort && filters.sort.direction
            ? {
                  sort_order: filters.sort.direction,
              }
            : null),
    }

    if (filters.status !== "all") {
        const isActive = filters.status === "active"
        // @ts-expect-error
        result = { ...result, is_active: isActive }
    }

    if (filters.source !== "all") {
        // @ts-expect-error
        result = { ...result, source: filters.source }
    }

    return result
}

export const getPaymentRequestsDetailDictionary = (state: StateObject): Dictionary => {
    return getOrdersState(state).paymentRequestsDetail
}

export const getRowTitlesForPaymentSummary = (state: StateObject): PaymentSummaryRowTitles => {
    return {
        cardService: getReportDatapointTitle(state, "CARD_SURCHARGE_CUSTOM_TEXT", "Card Service Fee"),
        cashDiscount: getReportDatapointTitle(state, "CASH_DISCOUNT_CUSTOM_TEXT", "Cash Discount"),
        serviceFee: getReportDatapointTitle(state, "SERVICE_FEE_CUSTOM_TEXT", "Service Fee"),
    }
}

export const getOrderDetailForClipboard = (state: StateObject): OrderDetailForClipboard => {
    const orderDetail = getOrderDetail(state)
    const account = getSelectedAccount(state)
    const location = getLocationsAuthData(state).filter(loc => loc.id === orderDetail?.general?.locationId)
    const locationTimezone = !isEmpty(location) ? location[0].timezone : "UTC"
    const orderId = getEditOrderId(state)
    const createdUTC = orderDetail?.general?.timestampOpened
    return {
        accountId: account?.id ?? "unknown",
        accountName: account?.name ?? "unknown",
        orderId: orderId,
        orderNumber: orderDetail?.general?.orderNumber,
        orderSource: orderDetail?.general?.orderSource,
        orderStatus: orderDetail?.general?.orderStatus,
        locationId: orderDetail?.general?.locationId,
        locationName: orderDetail?.general?.locationName,
        created: momentTimezone.tz(createdUTC, "UTC").toISOString(),
        createdLocationTime: momentTimezone.tz(createdUTC, locationTimezone).format(),
        createdCzechTime: momentTimezone.tz(createdUTC, "Europe/Prague").format(),
        gateway: orderDetail?.transactions?.find(t => t.paymentType === "card")?.gateway,
    }
}
